import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {isDefined, isNumeric, isTrue, nonBlank, notDefined} from "./epic.utils";

// Source: https://github.com/validatorjs/validator.js/tree/master/src/lib
export class AccValidator {

  public static readonly TEXT_MAX_LENGTH: number = 100000;

  // Version 3 and 4
  private static readonly UUID_PATTERN: RegExp = /^[0-9A-F]{8}-[0-9A-F]{4}-[34][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

  // see one.epicsoft.acc.validation.EmailValidator.REGEX / !!! Add 'u' (unicode) !!!
  private static readonly EMAIL_PATTERN: RegExp = /^(?=.{1,64}@)[\p{L}0-9_+-]+(\.[\p{L}0-9_-]+)*@[^-][\p{L}0-9-]+(\.[\p{L}0-9-]+)*(\.\p{L}{2,})$/iu;

  public static readonly HYPERLINK_SCHEMAS: string[] = ["http", "https", "ftp", "ftps", "sftp"];

  // noinspection all
  private static readonly HYPERLINK_PATTERN: RegExp = /^(http(s)?:\/\/|ftp(s)?:\/\/|sftp:\/\/)?(?:www\.)?(?!.*\.\.)[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

  public static readonly UUID: ValidatorFn = Validators.pattern(AccValidator.UUID_PATTERN);

  public static readonly price: ValidatorFn = Validators.pattern(/^-?\d{1,14}([.,]\d{0,5})?$/i);

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static isUUID(value: any): boolean {
    // Performance: string length check first
    return isDefined(value) && value.length === 36 ? AccValidator.UUID_PATTERN.test(value) : false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static isNumeric(value: any): boolean {
    if (typeof value === 'number') {
      return value !== -Infinity && value !== Infinity && !isNaN(value);
    } else if (typeof value === 'string') {
      return !!(value.match(/^(\d+\.)?\d+$/g));
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static isHyperlink(value: any): boolean {
    if (typeof value === 'string') {
      if (value.includes(":") && !AccValidator.HYPERLINK_SCHEMAS.some((s) => value.startsWith(s + "://"))) {
        return false;
      }
      return AccValidator.HYPERLINK_PATTERN.test(value);
    }
    return false;
  }

  public static hyperlink(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: any = control.value;
      if (notDefined(value)) {
        return null;
      }
      if (AccValidator.isHyperlink(value)) {
        return null;
      }
      return {hyperlink: {value: control.value}};
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static isInteger(value: any): boolean {
    return !!(String(value).match(/^\d+$/g));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public static isEmail(value: any): boolean {
    if (typeof value === 'string') {
      return AccValidator.EMAIL_PATTERN.test(value);
    }
    return false;
  }

  public static email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: any = control.value;
      if (notDefined(value)) {
        return null;
      }
      const email: any = typeof value === 'string' ? value.trim() : value;
      if (AccValidator.isEmail(email)) {
        return null;
      }
      return {email: {value: control.value}};
    };
  }

  public static numeric(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: any = control.value;
      if (notDefined(value)) {
        return null;
      }
      if (AccValidator.isNumeric(value)) {
        return null;
      }
      return {numeric: {value: control.value}};
    };
  }

  public static integer(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: any = control.value;
      if (notDefined(value)) {
        return null;
      }
      if (AccValidator.isInteger(value)) {
        return null;
      }
      return {integer: {value: control.value}};
    };
  }

  public static constructionDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: any = control.value;
      if (notDefined(value)) {
        return null;
      }
      if (value.match(/^(\d{2}\.)?(\d{2}\.)?\d{4}$/g)) {
        return null;
      }
      return {constructionDate: {value: control.value}};
    };
  }

  // Validates that at least one of the specified fields is set.
  public static atLeastOneRequiredValidator(fields: string[], options?: {
    allowFalse?: boolean;
    allowZero?: boolean
  }): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const values: Record<string, any> = {};
      fields.forEach((f) => values[f] = control.get(f)?.value);
      const hasValidValue: boolean = fields.some((f) => AccValidator.isMeaningfulValue(values[f], options));
      return hasValidValue ? null : {atLeastOneRequired: {fields: fields, values: values,}};
    };
  }

  private static isMeaningfulValue(value: any, options?: { allowFalse?: boolean; allowZero?: boolean }): boolean {
    if (notDefined(value)) {
      return false;
    } else if (typeof value === 'boolean') {
      return options?.allowFalse ? true : isTrue(value);
    } else if (typeof value === 'string') {
      return nonBlank(value);
    } else if (typeof value === 'number') {
      return options?.allowZero ? true : isNumeric(value) && value !== 0;
    }
    return true;
  }
}
